<script setup lang="ts">
/**
 * ConfiguratorViewer - Wrapper du Vue360Viewer connecté au materialStore
 * Transforme les données du store en props pour le viewer 360°
 */

import { computed, watch, ref, onMounted } from 'vue'
import { useMaterialStore } from '@/stores/materialStore'
import { useConfigStore } from '@/stores/configStore'
import { storeToRefs } from 'pinia'
import { Vue360Viewer, type MaterialLayer } from '@/components/viewer'
import InteriorViewer3D from '@/components/viewer/InteriorViewer3D.vue'
import ViewerControls from '@/components/viewer/ViewerControls.vue'

// import AccessoriesMenu from './AccessoriesMenu.vue'

defineOptions({
  name: 'ConfiguratorViewer'
})

/**
 * Props du composant
 */
interface Props {
  /** Chemin de base des matériaux (optionnel, défaut: /assets/materials/) */
  basePath?: string
  /** Nombre total d'images par rotation (par défaut: 36) */
  numAngles?: number
  /** Nombre d'angles affichables (par défaut: 12) */
  numAngleStep?: number
  /** Angle initial (par défaut: 0) */
  initialAngle?: number
  /** Mode de vue actuel */
  viewMode?: 'exterior' | 'interior'
}

const componentProps = withDefaults(defineProps<Props>(), {
  basePath: `${import.meta.env.BASE_URL}large/materials/`,
  numAngles: 36,
  numAngleStep: 36,
  initialAngle: 0,
  viewMode: 'exterior'
})

// Destructurer les props NON RÉACTIFS pour utilisation dans le template
const { numAngles, numAngleStep, initialAngle } = componentProps
// viewMode doit rester réactif, donc on le référence depuis componentProps

/**
 * Événements émis
 */
interface Emits {
  /** Émis lors du changement d'angle */
  (e: 'angleChange', angle: number): void
  /** Émis lors de la progression du chargement */
  (e: 'loadProgress', progress: number): void
  /** Émis lorsque toutes les images sont chargées */
  (e: 'loadComplete'): void
}

const emit = defineEmits<Emits>()

// Store des matériaux
const materialStore = useMaterialStore()
const {
  selectedExteriorColor,
  selectedInteriorColor,
  // accessories,
  activeAccessories
} = storeToRefs(materialStore)

// Store de configuration (contient le grade actif)
const configStore = useConfigStore()
const { currentGrade: activeGrade } = storeToRefs(configStore)

// Angle courant (pour affichage optionnel)
const currentAngle = ref(componentProps.initialAngle)

// État pour déclencher la rotation 360°
const trigger360Rotation = ref(false)

// État pour les contrôles 360°
const viewer360Angle = ref(componentProps.initialAngle)
const viewer360LoadedAngles = ref<Set<number>>(new Set())

// Référence au composant Vue360Viewer
const viewer360Ref = ref<InstanceType<typeof Vue360Viewer> | null>(null)

// État de chargement du viewer 360°
const isViewerLoading = ref(false)

onMounted(async () => {
  materialStore.initAccessories()

  // Les matériaux sont déjà chargés par configStore.selectGrade()
  // Pas besoin de les recharger ici
})

/**
 * Computed : Matériaux extérieurs pour vue extérieure
 * NOTE: Le calque "Wheel_001" est masqué car il masque les roues visibles
 */
const exteriorMaterialsWithAccessories = computed(() => {
  const materials: MaterialLayer[] = []

  // Layer 0: Base EXT-GRD
  if (selectedExteriorColor.value) {
    materials.push({
      material: selectedExteriorColor.value,
      layer: 0
    })
  }

  // Layers 1+: Accessoires actifs
  // Filtrer les matériaux Wheel_001 qui masquent les roues visibles
  activeAccessories.value.forEach((acc, index) => {
    // Exclure EXT-Wheel_001 qui masque les roues
    if (acc.material.includes('Wheel_001')) {
      console.log(`[ConfiguratorViewer] Calque ${acc.material} masqué (masque les roues visibles)`)
      return
    }
    materials.push({
      material: acc.material,
      layer: index + 1
    })
  })

  return materials
})

/**
 * Computed : Chemin du répertoire des matériaux
 */
const materialDirPath = computed(() => componentProps.basePath)

const interiorCubeFaces = computed(() => {
  if (!selectedInteriorColor.value) {
    // Retourner undefined pour que le composant ne soit pas monté
    return undefined
  }
  const material = selectedInteriorColor.value
  const basePath = `${materialDirPath.value}${material}/${material}`

  // Correspondances des faces du cubemap selon l'ordre legacy
  return {
    right: `${basePath}_01.jpg`,   // +X
    back: `${basePath}_02.jpg`,    // -Z
    front: `${basePath}_03.jpg`,   // +Z
    left: `${basePath}_04.jpg`,    // -X
    top: `${basePath}_05.jpg`,     // +Y
    bottom: `${basePath}_06.jpg`   // -Y
  }
})
/**
 * Computed : Y a-t-il des matériaux extérieurs à afficher ?
 */
const hasExteriorMaterials = computed(() => exteriorMaterialsWithAccessories.value.length > 0)

/**
 * Liste des grades nécessitant une image fixe intérieure
 * (en attendant les matériaux intérieurs corrects)
 */
const GRADES_WITH_FIXED_INTERIOR = ['GRD_P10GL', 'GRD_P22GL', 'GRD_P22GLX']

/**
 * Chemin de l'image fixe intérieure
 */
const interiorFixedImage = `${import.meta.env.BASE_URL}large/image/visu_int_fixe.png`

/**
 * Computed : Le grade actif nécessite-t-il une image fixe intérieure ?
 */
const useFixedInteriorImage = computed(() => {
  const shouldUseFixed = activeGrade.value && GRADES_WITH_FIXED_INTERIOR.includes(activeGrade.value.item_id)
  console.log('[ConfiguratorViewer] Grade actif:', activeGrade.value?.item_id, '| Utiliser image fixe:', shouldUseFixed)
  return shouldUseFixed
})

/**
 * Computed : Mode de vue réactif
 */
const currentViewMode = computed(() => componentProps.viewMode)

/**
 * Handler pour le changement d'angle
 */
function handleAngleChange(angle: number): void {
  currentAngle.value = angle
  viewer360Angle.value = angle
  emit('angleChange', angle)
}

/**
 * Vérifier si un angle 360° est chargé
 */
function isViewer360AngleLoaded(angle: number): boolean {
  return viewer360LoadedAngles.value.has(angle)
}

/**
 * Handler pour aller à l'angle précédent
 */
function handleViewer360Prev(): void {
  const newAngle = viewer360Angle.value > 0 ? viewer360Angle.value - 1 : componentProps.numAngleStep - 1
  viewer360Angle.value = newAngle
  // Synchroniser avec Vue360Viewer via la référence
  if (viewer360Ref.value) {
    viewer360Ref.value.setAngle(newAngle)
  }
}

/**
 * Handler pour aller à l'angle suivant
 */
function handleViewer360Next(): void {
  const newAngle = viewer360Angle.value < componentProps.numAngleStep - 1 ? viewer360Angle.value + 1 : 0
  viewer360Angle.value = newAngle
  // Synchroniser avec Vue360Viewer via la référence
  if (viewer360Ref.value) {
    viewer360Ref.value.setAngle(newAngle)
  }
}

/**
 * Handler pour aller à un angle spécifique
 */
function handleViewer360GoToAngle(angle: number): void {
  viewer360Angle.value = angle
  // Synchroniser avec Vue360Viewer via la référence
  if (viewer360Ref.value) {
    viewer360Ref.value.setAngle(angle)
  }
}

/**
 * Handler pour la progression du chargement
 */
function handleLoadProgress(progress: number): void {
  isViewerLoading.value = progress < 100
  emit('loadProgress', progress)
}

/**
 * Handler pour la fin du chargement
 */
function handleLoadComplete(): void {
  isViewerLoading.value = false
  emit('loadComplete')
}

/**
 * Handler pour le bouton flottant 3D - déclenche une rotation complète 360°
 */
function handle360Rotation(): void {
  // Déclencher la rotation en passant trigger360Rotation à true
  trigger360Rotation.value = true

  // Remettre à false après un court délai pour permettre de redéclencher
  setTimeout(() => {
    trigger360Rotation.value = false
  }, 100)

  console.log('Rotation 360° déclenchée')
}


/**
 * Watcher : Réagir aux changements de grade
 * Les matériaux sont chargés automatiquement par configStore.selectGrade()
 */
watch(
  () => activeGrade.value,
  (newGrade, oldGrade) => {
    if (newGrade && newGrade !== oldGrade) {
      console.log('Grade changé:', newGrade.item_id)
    }
  }
)

/**
 * Watcher : Réagir aux changements de matériaux
 * Le viewer se rechargera automatiquement grâce à son watcher interne
 */
watch(
  () => materialStore.selectedMaterials,
  (newSelection) => {
    console.log('Sélection de matériaux mise à jour:', newSelection)
  },
  { deep: true }
)
</script>

<template>
  <div class="configurator-viewer">

    <!-- Menu accessoires (toujours visible en vue extérieure) -->
    <!-- <AccessoriesMenu /> -->

    <div class="viewer-wrapper">
      <!-- Bouton flottant 3D (seulement en vue extérieure) -->
      <div class="floating-buttons">
        <button
          v-if="currentViewMode === 'exterior'"
          class="floating-3d-button"
          :disabled="isViewerLoading"
          @click="handle360Rotation"
          title="Rotation complète 360°"
        >
          <svg width="59" height="29" viewBox="0 0 59 29" fill="none" xmlns="http://www.w3.org/2000/svg">
            <mask id="mask0_833_1576" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="3" width="59" height="26">
            <path d="M16.7583 11.1633V7.72846L3.66279 12.6661L0.227909 22.9707C2.23159 24.3304 6.58243 27.3073 7.95638 28.3377C9.33033 29.3682 21.6959 28.7671 27.7069 28.3377L48.7455 23.8294L58.1914 10.0899L52.1804 3.86423H39.2996L40.8024 7.08442L50.2483 10.0899C49.4611 11.8789 47.7151 15.5857 47.0281 16.101C46.3411 16.6162 35.1492 20.0368 29.639 21.6826L8.8151 22.9707L6.88298 17.6037L16.7583 11.1633Z" fill="#D9D9D9"/>
            </mask>
            <g mask="url(#mask0_833_1576)">
            <path d="M26.4964 6.652C33.3655 5.35114 39.7679 5.19128 44.5626 5.9788C46.9609 6.37274 48.9458 7.00195 50.3836 7.83433C51.8217 8.66701 52.6885 9.68795 52.9107 10.8603C53.1328 12.0329 52.6995 13.3007 51.6652 14.6017C50.6314 15.9022 49.014 17.2136 46.9258 18.4572C42.7511 20.9433 36.7335 23.1356 29.8645 24.4365C22.9956 25.7373 16.5938 25.897 11.7992 25.1095C9.4009 24.7156 7.416 24.0863 5.97824 23.254C4.53985 22.4212 3.67301 21.3996 3.45095 20.227C3.22907 19.0546 3.66251 17.7874 4.69663 16.4866C5.73047 15.1861 7.34785 13.8747 9.43602 12.6311C13.6107 10.145 19.6276 7.95287 26.4964 6.652Z" stroke="white" stroke-width="0.42936"/>
            </g>
            <path d="M19.8484 17.8004C20.0491 17.8004 20.2575 17.7695 20.4736 17.7078C20.7051 17.6306 20.9135 17.5071 21.0987 17.3373C21.284 17.1521 21.4383 16.9051 21.5618 16.5964C21.6853 16.2723 21.747 15.8632 21.747 15.3693C21.747 14.7364 21.6621 14.2348 21.4924 13.8643C21.338 13.4938 21.1296 13.216 20.8672 13.0308C20.6202 12.8301 20.3424 12.7066 20.0337 12.6603C19.725 12.5986 19.424 12.5677 19.1307 12.5677H18.3898V10.2755H19.3159C19.5166 10.2755 19.7404 10.2446 19.9874 10.1829C20.2343 10.1211 20.4582 10.0131 20.6588 9.85872C20.8749 9.68892 21.0524 9.46511 21.1914 9.18726C21.3457 8.89398 21.4229 8.52353 21.4229 8.07589C21.4229 7.64369 21.3689 7.29638 21.2608 7.03398C21.1528 6.75613 21.0138 6.54003 20.844 6.38568C20.6897 6.23132 20.5122 6.13099 20.3115 6.08468C20.1109 6.02294 19.9256 5.99206 19.7558 5.99206C19.2773 5.99206 18.8683 6.16186 18.5287 6.50144C18.2045 6.84103 18.0039 7.31954 17.9267 7.93697L14.8936 7.54336C15.0325 6.94136 15.2563 6.40111 15.565 5.9226C15.8737 5.42866 16.2442 5.01189 16.6764 4.67231C17.124 4.31729 17.618 4.04716 18.1582 3.86193C18.7139 3.66127 19.3082 3.56093 19.9411 3.56093C20.5585 3.56093 21.1528 3.64583 21.7239 3.81562C22.3104 3.96998 22.8198 4.22467 23.252 4.57969C23.6997 4.91928 24.0547 5.3592 24.3171 5.89945C24.5795 6.42426 24.7107 7.04941 24.7107 7.77489V7.89066C24.7107 8.3846 24.6181 8.82452 24.4329 9.21042C24.2476 9.59631 24.0161 9.9359 23.7383 10.2292C23.4604 10.507 23.1517 10.7386 22.8121 10.9238C22.488 11.0936 22.1792 11.2093 21.886 11.2711C22.0094 11.302 22.1561 11.3405 22.3259 11.3869C22.4957 11.4332 22.6809 11.4949 22.8816 11.5721C23.1748 11.6956 23.4527 11.8654 23.7151 12.0815C23.9775 12.2976 24.209 12.56 24.4097 12.8687C24.6104 13.1774 24.7724 13.5324 24.8959 13.9338C25.0194 14.3351 25.0812 14.7904 25.0812 15.2998C25.0812 16.1797 24.9345 16.9283 24.6412 17.5457C24.348 18.1631 23.9621 18.6725 23.4836 19.0739C23.0051 19.4752 22.4571 19.7685 21.8397 19.9537C21.2377 20.1389 20.6202 20.2315 19.9874 20.2315C19.2156 20.2315 18.5364 20.1235 17.9498 19.9074C17.3787 19.6758 16.8848 19.3749 16.468 19.0044C16.0513 18.6339 15.7194 18.2095 15.4724 17.7309C15.2254 17.2524 15.0556 16.7585 14.963 16.2491L18.0193 15.8555C18.0347 16.1025 18.0888 16.3417 18.1814 16.5733C18.274 16.8048 18.3975 17.0132 18.5518 17.1984C18.7062 17.3836 18.8914 17.5303 19.1075 17.6383C19.3236 17.7464 19.5706 17.8004 19.8484 17.8004ZM32.6533 3.3294C33.6412 3.3294 34.5828 3.45288 35.478 3.69986C36.3733 3.93139 37.1528 4.36359 37.8166 4.99646C38.4957 5.61389 39.036 6.46285 39.4373 7.54336C39.8386 8.60842 40.0393 9.9822 40.0393 11.6647C40.0393 13.3472 39.8386 14.7287 39.4373 15.8092C39.036 16.8743 38.4957 17.7232 37.8166 18.3561C37.1528 18.9735 36.3733 19.4057 35.478 19.6527C34.5982 19.8842 33.6721 20 32.6996 20H28.3236V3.3294H32.6533ZM31.7966 5.82999V17.4763H32.6996C33.1627 17.4763 33.618 17.4068 34.0657 17.2679C34.5287 17.1135 34.9378 16.828 35.2928 16.4112C35.6633 15.9944 35.9565 15.4079 36.1726 14.6515C36.4042 13.8952 36.5199 12.8996 36.5199 11.6647C36.5199 10.4453 36.4042 9.45739 36.1726 8.70104C35.9411 7.92925 35.6401 7.33497 35.2697 6.91821C34.9146 6.48601 34.5056 6.20045 34.0425 6.06152C33.5794 5.90717 33.1164 5.82999 32.6533 5.82999H31.7966Z" fill="white"/>
            <path d="M6.23331 11.4269C6.26033 11.3115 6.18864 11.196 6.0732 11.169L4.19192 10.7287C4.07647 10.7017 3.96099 10.7734 3.93397 10.8888C3.90695 11.0042 3.97863 11.1197 4.09408 11.1468L5.76632 11.5381L5.37495 13.2104C5.34793 13.3258 5.41961 13.4413 5.53506 13.4683C5.6505 13.4954 5.76599 13.4237 5.79301 13.3082L6.23331 11.4269ZM0.227924 19.7505L0.441637 19.7302C0.307699 18.3238 1.21219 14.6175 6.13749 11.5604L6.02428 11.378L5.91106 11.1956C0.875224 14.3213 -0.138091 18.1717 0.0142114 19.7709L0.227924 19.7505Z" fill="white"/>
            <path d="M49.8246 19.9163C49.7975 20.0317 49.8692 20.1472 49.9847 20.1742L51.8659 20.6145C51.9814 20.6416 52.0969 20.5699 52.1239 20.4544C52.1509 20.339 52.0792 20.2235 51.9638 20.1965L50.2915 19.8051L50.6829 18.1329C50.7099 18.0174 50.6383 17.9019 50.5228 17.8749C50.4074 17.8479 50.2919 17.9196 50.2649 18.035L49.8246 19.9163ZM55.8299 11.5927L55.6162 11.6131C55.7502 13.0194 54.8457 16.7257 49.9204 19.7828L50.0336 19.9652L50.1468 20.1476C55.1826 17.0219 56.196 13.1715 56.0437 11.5723L55.8299 11.5927Z" fill="white"/>
            </svg>
        </button>
      </div>

      <!-- Vue extérieure -->
      <Vue360Viewer
        ref="viewer360Ref"
        v-if="currentViewMode === 'exterior' && hasExteriorMaterials"
        :materials="exteriorMaterialsWithAccessories"
        :materialDirPath="materialDirPath"
        :numAngles="numAngles"
        :numAngleStep="numAngleStep"
        :initialAngle="initialAngle"
        :trigger360Rotation="trigger360Rotation"
        :show-controls="false"
        @angleChange="handleAngleChange"
        @loadProgress="handleLoadProgress"
        @loadComplete="handleLoadComplete"
      />

      <!-- Vue intérieure - Image fixe pour grades Avantage/Privilège -->
      <div
        v-else-if="currentViewMode === 'interior' && useFixedInteriorImage"
        class="interior-fixed-image"
      >
        <img
          :src="interiorFixedImage"
          alt="Vue intérieure"
        />
      </div>

      <!-- Vue intérieure - Viewer 3D -->
      <InteriorViewer3D
        v-else-if="currentViewMode === 'interior' && interiorCubeFaces"
        :cubeMapFaces="interiorCubeFaces"
        @loadProgress="handleLoadProgress"
        @loadComplete="handleLoadComplete"
      />

      <!-- Placeholder quand aucune couleur sélectionnée -->
      <div v-else class="viewer-placeholder">
        <div class="placeholder-content">
          <svg
            class="placeholder-icon"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <h3 class="placeholder-title">Aucune couleur sélectionnée</h3>
          <p class="placeholder-text">
            Sélectionnez une couleur extérieure ou intérieure pour visualiser le véhicule
          </p>
        </div>
      </div>
    </div>

    <!-- Contrôles de navigation 360° (seulement en vue extérieure) -->
    <ViewerControls
      v-if="currentViewMode === 'exterior'"
      :current-angle="viewer360Angle"
      :num-angle-step="numAngleStep"
      :is-angle-loaded="isViewer360AngleLoaded"
      :disabled="isViewerLoading"
      @prev="handleViewer360Prev"
      @next="handleViewer360Next"
      @go-to-angle="handleViewer360GoToAngle"
    />

  </div>
</template>

<style scoped>
.configurator-viewer {
  position: relative;
  width: 100%;
  height: 100%;
}

/* Placeholder */
.viewer-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(
    135deg,
    rgba(240, 240, 240, 0.8) 0%,
    rgba(250, 250, 250, 0.8) 100%
  );
  border-radius: 8px;
}

.placeholder-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 32px;
  text-align: center;
  max-width: 400px;
}

.placeholder-icon {
  width: 64px;
  height: 64px;
  color: #9ca3af;
  opacity: 0.6;
}

.placeholder-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  color: #374151;
}

.placeholder-text {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}


.viewer-wrapper {
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Image fixe intérieure (grades Avantage/Privilège) */
.interior-fixed-image {
  position: relative;
  width: 100%;
  max-width: 875px;
  height: auto;
  max-height: 525px;
  aspect-ratio: 875 / 525;
  overflow: hidden;
  background-color: #000000;
  cursor: default;
}

.interior-fixed-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* Conteneur des boutons flottants */
.floating-buttons {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2000;
}

/* Bouton flottant 3D */
.floating-3d-button {
  cursor: pointer;
  border: none;
  box-shadow: none;
  padding: 0;
  transition: transform 0.2s ease;
}

.floating-3d-button:hover {
  transform: translateY(-1px);
}

.floating-3d-button:active {
  transform: translateY(0);
}

.floating-3d-button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  pointer-events: none;
}

.floating-3d-button svg {
  width: 56px;
  height: 29px;
}

/* Responsive */
@media (max-width: 768px) {

  .placeholder-content {
    padding: 24px;
  }

  .placeholder-icon {
    width: 48px;
    height: 48px;
  }

  .placeholder-title {
    font-size: 18px;
  }

  .placeholder-text {
    font-size: 13px;
  }

  .floating-buttons {
    top: 12px;
    right: 12px;
  }

  .floating-3d-button svg {
    width: 56px;
    height: 29px;
  }
}
</style>
